import { removePushDevice } from './push-api';

const BINDING_KEY = 'aztec-realms:push-installation-v1';
const REVOKE_KEY = 'aztec-realms:push-revocations-v1';
const MAX_QUEUE = 5;
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export interface InstallationBinding {
  ownerId: string;
  deviceId: string;
}
interface Revocation extends InstallationBinding {
  expires: number;
}

export function pushCapability(): 'unsupported' | NotificationPermission {
  return typeof window !== 'undefined' &&
    window.isSecureContext &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
    ? Notification.permission
    : 'unsupported';
}

export function currentPushBinding(): InstallationBinding | undefined {
  try {
    const parsed = JSON.parse(localStorage.getItem(BINDING_KEY) ?? 'null');
    return parsed && UUID.test(parsed.ownerId) && UUID.test(parsed.deviceId)
      ? { ownerId: parsed.ownerId, deviceId: parsed.deviceId }
      : undefined;
  } catch {
    return undefined;
  }
}

function revocations(): Revocation[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(REVOKE_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed
          .filter(
            (item) =>
              item &&
              UUID.test(item.ownerId) &&
              UUID.test(item.deviceId) &&
              Number.isFinite(item.expires) &&
              item.expires > Date.now(),
          )
          .slice(-MAX_QUEUE)
          .map((item) => ({
            ownerId: item.ownerId,
            deviceId: item.deviceId,
            expires: item.expires,
          }))
      : [];
  } catch {
    return [];
  }
}

async function registration() {
  return 'serviceWorker' in navigator ? navigator.serviceWorker.getRegistration('/') : undefined;
}

export async function setWorkerPushBinding(deviceId: string | null) {
  const active = (await registration())?.active;
  if (!active) {
    if (deviceId)
      throw new Error('Open the installed app again after its update, then enable notifications.');
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => {
      channel.port1.close();
      reject(new Error('The app needs an update before notifications can be enabled.'));
    }, 3000);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      channel.port1.close();
      if (event.data?.ok) resolve();
      else reject(new Error('The notification binding could not be saved.'));
    };
    active.postMessage({ type: 'AZR_PUSH_BIND', device_id: deviceId }, [channel.port2]);
  });
}

export async function bindPushInstallation(ownerId: string, deviceId: string) {
  if (!UUID.test(ownerId) || !UUID.test(deviceId)) throw new Error('Invalid notification binding.');
  await setWorkerPushBinding(deviceId);
  try {
    localStorage.setItem(BINDING_KEY, JSON.stringify({ ownerId, deviceId }));
  } catch (error) {
    await setWorkerPushBinding(null);
    throw error;
  }
}

/** No credentials/endpoints are queued. Revoke only under the original authenticated owner. */
export async function flushPushRevocations(ownerId: string) {
  if (!navigator.onLine) return;
  for (const item of revocations().filter((entry) => entry.ownerId === ownerId)) {
    try {
      await removePushDevice(item.deviceId);
      localStorage.setItem(
        REVOKE_KEY,
        JSON.stringify(revocations().filter((entry) => entry.deviceId !== item.deviceId)),
      );
    } catch {
      return;
    }
  }
}

export async function unbindPushInstallation(online: boolean) {
  const current = currentPushBinding();
  // Clear local identity before any network work; logout never waits for provider delivery.
  try {
    localStorage.removeItem(BINDING_KEY);
  } catch {
    /* Continue worker-side revocation. */
  }
  if (current) {
    try {
      const queued = revocations().filter((item) => item.deviceId !== current.deviceId);
      localStorage.setItem(
        REVOKE_KEY,
        JSON.stringify(
          [...queued, { ...current, expires: Date.now() + MAX_AGE }].slice(-MAX_QUEUE),
        ),
      );
    } catch {
      /* Worker unbinding and browser unsubscribe still suppress this installation. */
    }
  }
  const reg = await registration();
  await Promise.allSettled([
    setWorkerPushBinding(null),
    reg
      ?.getNotifications?.()
      .then((notifications) => notifications.forEach((notification) => notification.close())),
    reg?.pushManager?.getSubscription().then((subscription) => subscription?.unsubscribe()),
  ]);
  if (current && online) await flushPushRevocations(current.ownerId);
}

export async function reconcilePushOwner(ownerId: string) {
  const current = currentPushBinding();
  if (current && current.ownerId !== ownerId) await unbindPushInstallation(false);
  else if (current && pushCapability() === 'denied') await unbindPushInstallation(navigator.onLine);
  await flushPushRevocations(ownerId);
}

export async function subscribePushBrowser(applicationServerKey: string) {
  if (pushCapability() !== 'granted')
    throw new Error('Allow notifications in your browser first. Your inbox remains available.');
  const reg = await registration();
  if (!reg?.active)
    throw new Error('The app is still preparing offline support. Reopen it and try again.');
  // Handshake proves this is the updated existing worker before obtaining a subscription.
  await setWorkerPushBinding(null);
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  const padded = applicationServerKey.replace(/-/g, '+').replace(/_/g, '/');
  const key = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}
